'use client';

import { ArrowForward } from '@mui/icons-material';
import {
  Box,
  Button,
  Container,
  InputAdornment,
  InputBase,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import Link from 'next/link';
import { ChangeEvent } from 'react';

import { newHomepageTypography } from './newHomepageStyles';

interface FinalCtaSectionProps {
  rawAmount: string;
  finalHref: string;
  onAmountChange: (_event: ChangeEvent<HTMLInputElement>) => void;
}

export function FinalCtaSection({ rawAmount, finalHref, onAmountChange }: FinalCtaSectionProps) {
  const theme = useTheme();

  return (
    <Box
      component="section"
      sx={{
        background: theme.palette.brand.primary,
        color: theme.palette.common.white,
        px: { xs: 2, md: 0 },
        py: { xs: 7, md: 10 },
      }}
    >
      <Container maxWidth="xl">
        <Box
          sx={{
            maxWidth: 760,
            mx: 'auto',
            textAlign: 'center',
          }}
        >
          <Typography
            component="h2"
            sx={{
              ...newHomepageTypography.h2,
              fontWeight: 800,
              letterSpacing: 0,
              mb: 2,
            }}
          >
            <Box component="span" sx={{ color: alpha(theme.palette.common.white, 0.62) }}>
              Ready to find the
            </Box>{' '}
            Perfect Lender?
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: 15, md: 16 },
              lineHeight: 1.5,
              color: alpha(theme.palette.common.white, 0.9),
              mb: { xs: 4, md: 4.5 },
            }}
          >
            Apply now, takes less than 5 minutes and secure the best loan. No broker needed.
          </Typography>

          <Box sx={{ maxWidth: 440, mx: 'auto', textAlign: 'left' }}>
            <Typography
              component="label"
              htmlFor="final-cta-amount"
              sx={{
                display: 'block',
                mb: 1.3,
                fontSize: { xs: 16, md: 18 },
                fontWeight: 800,
                color: theme.palette.common.white,
              }}
            >
              How much do you need to borrow
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'stretch',
                gap: { xs: 1.2, sm: 0 },
                borderRadius: { xs: 2, sm: 1.5 },
              }}
            >
              <InputBase
                id="final-cta-amount"
                value={rawAmount}
                onChange={onAmountChange}
                inputProps={{
                  inputMode: 'decimal',
                  pattern: '[0-9.]*',
                  'aria-label': 'Loan amount',
                }}
                startAdornment={
                  <InputAdornment position="start">
                    <Box
                      component="img"
                      src="/assets/new-homepage/search-loan-dollar.svg"
                      alt=""
                      aria-hidden="true"
                      sx={{ width: 32, height: 32, display: 'block' }}
                    />
                  </InputAdornment>
                }
                sx={{
                  flex: { xs: '1 1 auto', sm: '0 0 260px' },
                  height: 52,
                  pl: 1.3,
                  pr: 2,
                  background: theme.palette.common.white,
                  borderRadius: { xs: 1.5, sm: '12px 0 0 12px' },
                  color: theme.palette.brand.bodyText,
                  fontSize: 18,
                  fontWeight: 800,
                  '& input': { p: 0 },
                }}
              />
              <Button
                component={Link}
                href={finalHref}
                variant="contained"
                endIcon={<ArrowForward sx={{ fontSize: 18 }} />}
                sx={{
                  height: 52,
                  px: { xs: 3, sm: 3.5 },
                  minWidth: { sm: 150 },
                  borderRadius: { xs: 999, sm: '999px' },
                  background: theme.palette.brand.bodyText,
                  color: theme.palette.common.white,
                  fontSize: 13,
                  fontWeight: 800,
                  whiteSpace: 'nowrap',
                  boxShadow: 'none',
                  ml: { sm: -3 },
                  zIndex: 1,
                  '&:hover,&:focus': {
                    background: '#151023',
                    boxShadow: 'none',
                  },
                }}
              >
                Get Best Loan
              </Button>
            </Box>

            <Typography
              sx={{
                mt: 1.6,
                fontSize: 12,
                color: alpha(theme.palette.common.white, 0.78),
              }}
            >
              This process will not affect your credit rating in any way.
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
